<template>
  <div class="ui-counter__wrap">
    <div
      ref="wrapper"
      class="ui-counter__wrap-inner">
      <div
        :class="{
          'ui-counter--has-error': error,
        }"
        class="ui-counter__input-wrap">
        <div
          class="shape-cross ui-counter__icon ui-counter__icon--minus"
          @click="setValue(val - step)" />

        <label class="ui-counter__body">
          <input
            ref="input"
            :value="val"
            type="number"
            inputmode="numeric"
            :placeholder="placeholder"
            :disabled="disabled"
            :tabindex="tabindex"
            :name="name"
            :step="step"
            class="ui-counter__input"
            @focus="onFocus"
            @blur="onBlur"
            @keypress="onKeyPress"
            @paste="onPaste"
            @input="setValue($event.target.value, { skipMin: true })">

          <error-tooltip
            :error="error"
            :z-index="zIndexTooltip" />
        </label>

        <div
          class="shape-cross ui-counter__icon ui-counter__icon--plus"
          @click="setValue(val + step)" />
      </div>
    </div>
  </div>
</template>

<script>
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';

const inputNumberBlockedKeys = new RegExp(/[^0-9.-]/g);

export default {
  name: 'UiCounter',

  components: {
    ErrorTooltip,
  },

  props: {
    value: {
      type: [String, Number],
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    regexpFilter: {
      type: RegExp,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    min: {
      type: Number,
      default: null,
    },

    max: {
      type: Number,
      default: null,
    },

    tabindex: {
      type: [Number, String],
      default: null,
    },

    step: {
      type: Number,
      default: 1,
    },

    name: {
      type: String,
      default: null,
    },

    icon: {
      type: Object,
      default: () => ({}),
    },

    zIndexTooltip: {
      type: Number,
      default: 100,
    },

    focusOnMount: {
      type: Boolean,
      default: false,
    },

    isCurrency: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      val: this.value,
    };
  },

  watch: {
    value: {
      handler(newVal) {
        if (+this.val !== +newVal) {
          this.val = newVal;
        }
      },
    },
  },

  mounted() {
    if (this.focusOnMount) {
      this.focus();
    }
  },

  methods: {
    setValue(value, options) {
      this.val = +value;
      this.checkValue(options);

      this.$emit('input', this.val);
    },

    focus() {
      this.$refs.input.focus();
    },

    onFocus(event) {
      this.$emit('focus', event);
    },

    onBlur(event) {
      this.checkValue();
      this.$emit('input', this.val);
      this.$emit('blur', event);
    },

    onKeyPress(event) {
      if (event.key !== 'Enter' && event.key.match(inputNumberBlockedKeys)) {
        event.preventDefault();
        return;
      }

      const filtered = this.sanitizeValue(event.key);

      if (event.key !== filtered) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      event.preventDefault();

      const { clipboardData } = event.originalEvent || event;
      const content = clipboardData.getData('text/plain').replace(inputNumberBlockedKeys, '');

      document.execCommand('insertText', false, content);

      setTimeout(() => {
        const filtered = this.sanitizeValue(event.target.value);
        if (event.target.value !== filtered) {
          this.val = filtered;
        }
      }, 0);
    },

    sanitizeValue(value) {
      let filtered = value;

      if (this.regexpFilter) {
        filtered = filtered.replace(this.regexpFilter, '');
      }

      return filtered;
    },

    checkValue(options = {
      skipMax: false,
      skipMin: false,
    }) {
      if (!options.skipMin && Number.isFinite(this.min) && (+this.val < this.min)) {
        this.val = this.min;
      }

      if (!options.skipMax && Number.isFinite(this.max) && (+this.val > this.max)) {
        this.val = this.max;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .ui-counter__wrap {
    --prefix-color: #{$color--basic-white};
    //--border-radius: 3px;
    --font-weight: 400;
    //--font-size: #{$text-21px};
    --color: #{$color--basic-white};
    --placeholder-color: #{$color--basic-light-gray};
    --opacity: 1;
    --opacity--focused: 1;
    --icon-size: 16px;
    --icon-color: #{$color--basic-white};
    --icon-color--focused: #{$color--basic-white};
    --icon-margin: 0 0 0 0;
    //--height: 60px;
    //--padding: 8px 20px;
    //--background-color: #{$color--additional-menu};
    //--background-color--focused: #{$color--basic-superdark-gray};
  }

  .ui-counter__wrap-inner {
    position: relative;
    display: flex;
    align-items: center;
    height: var(--height, 60px);
    border-radius: var(--border-radius, 3px);
    background-color: var(--background-color, #{$color--additional-menu});
    padding: var(--padding, 8px 20px);
    opacity: var(--opacity);
    transition: background-color .2s ease-out, opacity .2s ease-out;

    /* https://caniuse.com/#search=focus-within */
    &:focus-within {
      opacity: var(--opacity--focused);
      background-color: var(--background-color--focused, #{$color--basic-superdark-gray});
    }
  }

  .ui-counter__input {
    flex-grow: 1;
    width: 100%;
    background-color: transparent;
    color: var(--color);
    font-size: var(--font-size, 21px);
    font-weight: var(--font-weight);
    font-family: Rubik, sans-serif;
    text-align: var(--text-align, left);
    border: none;
    outline: none;
    z-index: 2;
    position: relative;

    &::placeholder {
      color: var(--placeholder-color);
      font-weight: 500;
      opacity: 1 !important;
    }

    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type=number] {
      -moz-appearance: textfield;
    }

    // тут магия, даже не пытайтесь это понять, но выглядит круто
    &:-webkit-autofill,
    &:-webkit-autofill:hover {
      -webkit-text-fill-color: $color--basic-white !important;
      caret-color: $color--basic-white !important;
      color: $color--basic-white !important;
      background-clip: content-box !important; /* Safari наш любимый */
    }

    &:-webkit-autofill:hover,
    &:-webkit-autofill:empty:not(:focus) {
      transition: box-shadow .2s ease-out; // так делать очень плохо, но меня заставили
    }

    &:-webkit-autofill:focus {
      box-shadow: inset 0 0 0 30px var(--background-color--focused, #{$color--basic-superdark-gray}) !important;
    }

    &:-webkit-autofill:empty:not(:focus) {
      box-shadow: inset 0 0 0 30px var(--background-color, #{$color--additional-menu}) !important;
    }
  }

  .ui-counter__input-wrap {
    position: relative;
  }

  .ui-counter__body, .ui-counter__input-wrap {
    display: flex;
    flex: 1;
    align-items: center;
  }

  .ui-counter__icon {
    --transform-before: translate(-50%, -50%);

    width: 16px;
    height: 16px;
  }

  .ui-counter__icon--plus {
    --transform-after: translate(-50%, -50%) rotate(90deg);
  }

  .ui-counter__icon--minus {
    --transform-after: translate(-50%, -50%);
  }
</style>
